import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { ChatKeyUnavailableError } from '../utils/chatCrypto';
import { buildChatClearAuthMessage } from '../utils/roomConfigAuth';
import { getWalletSocketAuth, clearWalletSocketAuth } from '../utils/walletSocketAuth';
import { decryptSecretMessage, deriveSafetyNumber, directKeyAdvertisementMessage, encryptSecretMessage, generateSecretDeviceKeyPair, publicKeyFingerprint, } from '../utils/secretMessageCrypto';
import { createSecretDiagnosticsRecorder, } from '../utils/secretDiagnostics';
import { ethers } from "ethers";
/** Byte-exact, socket-bound authorization text for activating admin presence. */
export function buildAdminPresenceAuthMessage(room, challenge) {
    return `Dwinity admin presence authorization\nroom: ${room.toLowerCase()}\nchallenge: ${challenge}`;
}
const MAX_PERSISTED_MESSAGES = 200;
// Host-only call moderation signals: the relay only forwards these from a
// wallet-verified socket (see chatHandler.js) — everything else it relays
// unconditionally. Keep this list in sync with the relay's.
const MODERATION_SUBTYPES = [
    'mute-request', 'lk-mute-request', 'unmute-request', 'lk-unmute-request',
    'mute-one', 'lk-mute-one', 'unmute-one', 'lk-unmute-one',
    'kick', 'lk-kick',
];
const AUTHENTICATED_CALL_SUBTYPES = [
    ...MODERATION_SUBTYPES,
    'screen-share-claim', 'screen-share-release',
    'lk-screen-share-claim', 'lk-screen-share-release',
];
// Queued moderation signals older than this are stale (the host has moved
// on); drop instead of replaying a confusing late mute/unmute.
const PENDING_MODERATION_MAX_AGE_MS = 30 * 1000;
const SECRET_ACK_RETRY_DELAY_MS = 1_250;
const SECRET_ACK_MAX_ATTEMPTS = 3;
const SECRET_DELIVERY_TIMEOUT_MS = 8_000;
const SECRET_INCOMING_FRESHNESS_MS = 5 * 60 * 1000;
const MAX_PENDING_INCOMING_SECRETS = 100;
const MAX_PENDING_INCOMING_SECRETS_PER_SENDER = 20;
function getStorageKey(walletAddress) {
    return `dwdata_chat_messages_${walletAddress.toLowerCase()}`;
}
function loadPersistedMessages(walletAddress) {
    if (!walletAddress)
        return [];
    try {
        const raw = sessionStorage.getItem(getStorageKey(walletAddress));
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            console.log('[useChatWebSocket] Restored', parsed.length, 'messages from sessionStorage');
            return parsed;
        }
    }
    catch (err) {
        console.warn('[useChatWebSocket] Failed to load persisted messages:', err);
    }
    return [];
}
function persistMessages(walletAddress, messages) {
    if (!walletAddress)
        return;
    try {
        const toStore = messages.slice(-MAX_PERSISTED_MESSAGES).map(({ id, text, deviceId, timestamp, isMine, failedDecrypt, generation }) => ({
            id, text, deviceId, timestamp, isMine, generation,
            ...(failedDecrypt ? { failedDecrypt: true } : {}),
        }));
        sessionStorage.setItem(getStorageKey(walletAddress), JSON.stringify(toStore));
    }
    catch (err) {
        console.warn('[useChatWebSocket] Failed to persist messages:', err);
    }
}
export function useChatWebSocket({ walletAddress, encryptFn, decryptFn, onCallSignal, guestToken, signWalletAuth, signChatClear, deviceId: suppliedDeviceId, deviceSessionToken, onDeviceSessionRejected, adminPresenceActive = false, secretDiagnostics = false, }) {
    const normalizedRoom = walletAddress?.trim().toLowerCase() || null;
    const [messages, setMessages] = useState([]);
    const commonGenerationRef = useRef(null);
    const clearSignerRef = useRef(signChatClear);
    clearSignerRef.current = signChatClear;
    const clearRequestRef = useRef(null);
    const commonSendRequestsRef = useRef(new Map());
    const commonAckVersionRef = useRef(false);
    const rejectCommonRequests = useCallback((reason) => {
        commonAckVersionRef.current = false;
        clearRequestRef.current?.reject(new Error(reason));
        clearRequestRef.current = null;
        for (const pending of commonSendRequestsRef.current.values())
            pending.reject(new Error(reason));
        commonSendRequestsRef.current.clear();
    }, []);
    const [isConnected, setIsConnected] = useState(false);
    const [connectedDevices, setConnectedDevices] = useState(0);
    const [roomDevices, setRoomDevices] = useState([]);
    const [organizerPresent, setOrganizerPresent] = useState(false);
    const [archiveEnabled, setArchiveEnabledState] = useState(false);
    // Legacy-relay detection: old relays never send `room_config`, so the
    // archive state stays unknown. Once any room_config arrives we trust the
    // relay for the rest of the session.
    const [archiveStateUnknown, setArchiveStateUnknown] = useState(false);
    const roomConfigReceivedRef = useRef(false);
    const roomConfigTimerRef = useRef(null);
    // Scheduler → room notices ("meeting starts soon" / "conference closed").
    // The relay re-broadcasts the warning every tick; dedupe by notice+startsAt
    // so the banner does not re-animate every 60 seconds.
    const [systemNotice, setSystemNotice] = useState(null);
    const lastNoticeKeyRef = useRef(null);
    // Direct-message plaintext deliberately never leaves React memory. In
    // particular, do not put this state in sessionStorage/localStorage: those
    // stores are readable by any script running in this origin.
    const [secretMessages, setSecretMessages] = useState({});
    const [secretMessageError, setSecretMessageError] = useState(null);
    const [guestAccessClosed, setGuestAccessClosed] = useState(false);
    const secretDiagnosticsRef = useRef(null);
    const secretDiagnosticsConfigRef = useRef(null);
    const secretDiagnosticsPropConfigRef = useRef(null);
    const requestedDiagnosticsConfig = {
        enabled: typeof secretDiagnostics === 'boolean'
            ? secretDiagnostics
            : secretDiagnostics.enabled === true,
        maxEvents: typeof secretDiagnostics === 'boolean'
            ? undefined
            : secretDiagnostics.maxEvents,
    };
    const previousRequestedDiagnosticsConfig = secretDiagnosticsPropConfigRef.current;
    if (!previousRequestedDiagnosticsConfig ||
        previousRequestedDiagnosticsConfig.enabled !== requestedDiagnosticsConfig.enabled ||
        previousRequestedDiagnosticsConfig.maxEvents !== requestedDiagnosticsConfig.maxEvents) {
        secretDiagnosticsRef.current?.clear();
        secretDiagnosticsRef.current = createSecretDiagnosticsRecorder(requestedDiagnosticsConfig);
        secretDiagnosticsConfigRef.current = requestedDiagnosticsConfig;
        secretDiagnosticsPropConfigRef.current = requestedDiagnosticsConfig;
    }
    const recordSecretDiagnostic = useCallback((event) => {
        secretDiagnosticsRef.current?.record(event);
    }, []);
    const getSecretDiagnostics = useCallback(() => secretDiagnosticsRef.current?.snapshot() || [], []);
    const clearSecretDiagnostics = useCallback(() => {
        secretDiagnosticsRef.current?.clear();
    }, []);
    const enableSecretDiagnostics = useCallback((options) => {
        secretDiagnosticsRef.current?.clear();
        secretDiagnosticsRef.current = createSecretDiagnosticsRecorder({
            enabled: true,
            maxEvents: options?.maxEvents,
        });
        secretDiagnosticsConfigRef.current = {
            enabled: true,
            maxEvents: options?.maxEvents,
        };
    }, []);
    const disableSecretDiagnostics = useCallback(() => {
        secretDiagnosticsRef.current?.clear();
        secretDiagnosticsRef.current = createSecretDiagnosticsRecorder({ enabled: false });
        secretDiagnosticsConfigRef.current = { enabled: false };
    }, []);
    const isSecretDiagnosticsEnabled = useCallback(() => secretDiagnosticsConfigRef.current?.enabled === true, []);
    const secretDiagnosticsControl = {
        enable: enableSecretDiagnostics,
        snapshot: getSecretDiagnostics,
        clear: clearSecretDiagnostics,
        disable: disableSecretDiagnostics,
        isEnabled: isSecretDiagnosticsEnabled,
    };
    const rescopeSecretDiagnostics = useCallback((reason) => {
        secretDiagnosticsRef.current?.clear();
        recordSecretDiagnostic({ kind: 'readiness_invalidated', reason });
    }, [recordSecretDiagnostic]);
    const guestAccessClosedRef = useRef(false);
    const secretKeyPairRef = useRef(null);
    const secretPeerKeysRef = useRef(new Map());
    const secretPeerExpiryTimersRef = useRef(new Map());
    const secretAdvertisementRefreshTimerRef = useRef(null);
    const localSecretAdvertisementRef = useRef(null);
    const secretAdvertisementInFlightRef = useRef(false);
    const secretAdvertisementSigningBlockedRef = useRef(false);
    const directKeyRelayAttestationRef = useRef(!!deviceSessionToken);
    const [secretPeerVersion, setSecretPeerVersion] = useState(0);
    const [secretConversationVersion, setSecretConversationVersion] = useState(0);
    const localSecretConversationOpenRef = useRef(new Set());
    const remoteSecretConversationOpenRef = useRef(new Set());
    const readySecretConversationsRef = useRef(new Set());
    // Keep the relay digest with the binding.  A retransmitted ciphertext is
    // acknowledged only when it is the exact envelope already authenticated by
    // AEAD; it must never cause a second plaintext render.
    const seenSecretIdsRef = useRef(new Map());
    // Ciphertext-only handoff queue. A relay message can beat the asynchronous
    // fingerprint/SAS work for the sender's advertisement. Never put decrypted
    // text (or a peer key) in this queue.
    const pendingIncomingSecretsRef = useRef(new Map());
    // The user may explicitly open a secret conversation before the relay
    // catch-up has delivered that peer's key. Keep this approval intent scoped
    // to the exact target device until its validated advertisement arrives.
    const pendingSecretApprovalsRef = useRef(new Set());
    const incomingSecretDecryptsRef = useRef(new Map());
    const incomingSecretContextRef = useRef(0);
    // Every structurally admissible advertisement supersedes the previous one
    // before its asynchronous fingerprint/SAS work starts. This prevents an old
    // promise from reinstalling an older epoch after a rotation.
    const latestSecretAdvertisementsRef = useRef(new Map());
    const secretAdvertisementContextRef = useRef(0);
    // This is deliberately socket-scoped. A new transport must not replay a
    // pending envelope until the relay confirms that this exact device key is
    // registered on that transport.
    const secretKeyRegisteredRef = useRef(false);
    const pendingSecretSendsRef = useRef(new Map());
    const wsRef = useRef(null);
    const deviceIdRef = useRef(suppliedDeviceId || `device-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const reconnectTimerRef = useRef(null);
    const pingTimerRef = useRef(null);
    const mountedRef = useRef(true);
    const encryptFnRef = useRef(encryptFn);
    const decryptFnRef = useRef(decryptFn);
    const onCallSignalRef = useRef(onCallSignal);
    const signWalletAuthRef = useRef(signWalletAuth);
    const walletAuthAckedRef = useRef(false);
    const walletAuthInFlightRef = useRef(false);
    const walletAuthTimerRef = useRef(null);
    const walletAuthAttemptRef = useRef(null);
    const guestTokenRef = useRef(guestToken);
    const deviceSessionTokenRef = useRef(deviceSessionToken);
    const onDeviceSessionRejectedRef = useRef(onDeviceSessionRejected);
    const adminPresenceActiveRef = useRef(adminPresenceActive);
    const walletAddressRef = useRef(walletAddress);
    const adminPresenceChallengeRef = useRef(null);
    const adminPresenceSigningRef = useRef(false);
    // A declined/rejected legacy signature must not cause a wallet prompt loop.
    // This is intentionally activation-scoped, rather than challenge/socket-
    // scoped: reconnecting while the chat UI remains open must not prompt again.
    const adminPresenceAttemptedChallengeRef = useRef(null);
    const adminPresenceLegacyAttemptedRef = useRef(false);
    const adminPresenceAckedRef = useRef(false);
    const adminPresenceRetryRef = useRef(null);
    const socketGenerationRef = useRef(0);
    const sendCallSignalRef = useRef(null);
    // Moderation signals (mute-all / unmute-all / kick) sent while the socket
    // is not yet wallet-verified would be SILENTLY dropped by the relay (live
    // incident 2026-08-12: after a reconnect the admin's unmute-all vanished
    // this way while mute-all had gone out on the previous, still-verified
    // socket). Queue them until the relay acks `wallet_auth_ok`, then flush.
    const pendingModerationRef = useRef([]);
    encryptFnRef.current = encryptFn;
    decryptFnRef.current = decryptFn;
    onCallSignalRef.current = onCallSignal;
    signWalletAuthRef.current = signWalletAuth;
    guestTokenRef.current = guestToken;
    deviceSessionTokenRef.current = deviceSessionToken;
    if (deviceSessionToken)
        directKeyRelayAttestationRef.current = true;
    onDeviceSessionRejectedRef.current = onDeviceSessionRejected;
    // A supplied registered identity is stable across socket reconnects.
    if (suppliedDeviceId)
        deviceIdRef.current = suppliedDeviceId;
    adminPresenceActiveRef.current = adminPresenceActive;
    walletAddressRef.current = walletAddress;
    const retransmitPendingSecretSends = useCallback((onlyId) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN || !secretKeyRegisteredRef.current)
            return;
        pendingSecretSendsRef.current.forEach((pending, id) => {
            if (onlyId && id !== onlyId)
                return;
            if (pending.attempts >= SECRET_ACK_MAX_ATTEMPTS)
                return;
            pending.attempts += 1;
            try {
                ws.send(JSON.stringify(pending.envelope));
            }
            catch {
                // The delivery deadline remains authoritative; a reconnect may still
                // provide another authenticated socket before it expires.
            }
        });
    }, []);
    const clearPendingIncomingSecrets = useCallback(() => {
        pendingIncomingSecretsRef.current.forEach(({ expiryTimer }) => clearTimeout(expiryTimer));
        pendingIncomingSecretsRef.current.clear();
        incomingSecretDecryptsRef.current.clear();
        incomingSecretContextRef.current += 1;
        latestSecretAdvertisementsRef.current.clear();
        pendingSecretApprovalsRef.current.clear();
        secretAdvertisementContextRef.current += 1;
    }, []);
    const clearSecretConversationState = useCallback(() => {
        pendingSecretApprovalsRef.current.clear();
        localSecretConversationOpenRef.current.clear();
        remoteSecretConversationOpenRef.current.clear();
        readySecretConversationsRef.current.clear();
        setSecretConversationVersion((value) => value + 1);
    }, []);
    const clearSecretStateForAuthBoundary = useCallback(() => {
        // Auth changes are a hard secret scope boundary. Do not let an
        // observation-map reset make a key/consent/plaintext pair look reusable
        // on the replacement authenticated socket.
        secretKeyPairRef.current = null;
        localSecretAdvertisementRef.current = null;
        secretAdvertisementInFlightRef.current = false;
        secretAdvertisementSigningBlockedRef.current = false;
        secretPeerKeysRef.current.clear();
        secretPeerExpiryTimersRef.current.forEach(clearTimeout);
        secretPeerExpiryTimersRef.current.clear();
        if (secretAdvertisementRefreshTimerRef.current) {
            clearTimeout(secretAdvertisementRefreshTimerRef.current);
        }
        secretAdvertisementRefreshTimerRef.current = null;
        secretKeyRegisteredRef.current = false;
        seenSecretIdsRef.current.clear();
        clearPendingIncomingSecrets();
        clearSecretConversationState();
        setSecretMessages({});
        setSecretMessageError(null);
        pendingSecretSendsRef.current.forEach(({ reject, deliveryTimer, retryTimer }) => {
            clearTimeout(deliveryTimer);
            if (retryTimer)
                clearTimeout(retryTimer);
            reject(new Error("Secret message authentication scope changed."));
        });
        pendingSecretSendsRef.current.clear();
    }, [clearPendingIncomingSecrets, clearSecretConversationState]);
    const processIncomingSecretRef = useRef(null);
    const processIncomingSecret = useCallback(async (data, queuedReplay = false) => {
        const room = walletAddressRef.current?.toLowerCase();
        if (!room ||
            data.room !== room ||
            data.recipientDeviceId !== deviceIdRef.current ||
            typeof data.senderDeviceId !== "string" ||
            typeof data.id !== "string" ||
            typeof data.timestamp !== "number" ||
            typeof data.encryptedPayload !== "string" ||
            typeof data.digest !== "string" ||
            Math.abs(Date.now() - data.timestamp) > SECRET_INCOMING_FRESHNESS_MS) {
            if (queuedReplay && typeof data.id === "string") {
                const expired = pendingIncomingSecretsRef.current.get(data.id);
                if (expired) {
                    clearTimeout(expired.expiryTimer);
                    pendingIncomingSecretsRef.current.delete(data.id);
                    setSecretMessageError("A pending secret message expired before its sender could be verified.");
                }
            }
            return;
        }
        const seenBinding = JSON.stringify([
            room, data.senderDeviceId, data.recipientDeviceId,
            data.id, data.timestamp, data.digest,
        ]);
        const conversationReady = readySecretConversationsRef.current.has(data.senderDeviceId);
        const seenBindingForId = seenSecretIdsRef.current.get(data.id);
        if (seenBindingForId !== undefined) {
            if (seenBindingForId === seenBinding) {
                const ackWs = wsRef.current;
                if (conversationReady && ackWs?.readyState === WebSocket.OPEN) {
                    ackWs.send(JSON.stringify({
                        type: "secret_message_ack", room, senderDeviceId: data.senderDeviceId,
                        recipientDeviceId: deviceIdRef.current, id: data.id, digest: data.digest,
                    }));
                }
            }
            else {
                setSecretMessageError("A conflicting secret message replay was rejected.");
            }
            return;
        }
        const pendingForId = pendingIncomingSecretsRef.current.get(data.id);
        if (pendingForId && !queuedReplay) {
            if (pendingForId.binding !== seenBinding || pendingForId.senderDeviceId !== data.senderDeviceId) {
                setSecretMessageError("A conflicting secret message replay was rejected.");
            }
            // Exact retransmissions remain represented by one ciphertext entry and
            // receive no ACK until that entry has passed AEAD verification.
            return;
        }
        const decryptingBinding = incomingSecretDecryptsRef.current.get(data.id);
        if (decryptingBinding !== undefined) {
            if (decryptingBinding !== seenBinding) {
                setSecretMessageError("A conflicting secret message replay was rejected.");
            }
            return;
        }
        const peer = secretPeerKeysRef.current.get(data.senderDeviceId);
        const pair = secretKeyPairRef.current;
        if (!peer || !pair || !conversationReady || (!peer.walletSigned && !peer.safetyConfirmed)) {
            if (queuedReplay)
                return;
            let senderCount = 0;
            pendingIncomingSecretsRef.current.forEach((entry) => {
                if (entry.senderDeviceId === data.senderDeviceId)
                    senderCount += 1;
            });
            if (pendingIncomingSecretsRef.current.size >= MAX_PENDING_INCOMING_SECRETS ||
                senderCount >= MAX_PENDING_INCOMING_SECRETS_PER_SENDER) {
                setSecretMessageError("Too many pending secret messages; the newest ciphertext was rejected.");
                return;
            }
            const expiresAt = Math.min(Date.now() + SECRET_INCOMING_FRESHNESS_MS, data.timestamp + SECRET_INCOMING_FRESHNESS_MS);
            const expiryTimer = setTimeout(() => {
                const current = pendingIncomingSecretsRef.current.get(data.id);
                if (!current || current.binding !== seenBinding)
                    return;
                pendingIncomingSecretsRef.current.delete(data.id);
                setSecretMessageError("A pending secret message expired before its sender could be verified.");
            }, Math.max(0, expiresAt - Date.now()));
            pendingIncomingSecretsRef.current.set(data.id, {
                // Copy only protocol ciphertext fields. Do not retain arbitrary relay
                // properties (especially anything claiming to be plaintext).
                data: {
                    type: "secret_message",
                    room: data.room,
                    senderDeviceId: data.senderDeviceId,
                    recipientDeviceId: data.recipientDeviceId,
                    id: data.id,
                    timestamp: data.timestamp,
                    encryptedPayload: data.encryptedPayload,
                    digest: data.digest,
                },
                binding: seenBinding,
                senderDeviceId: data.senderDeviceId,
                expiryTimer,
            });
            return;
        }
        if (peer.expiresAt <= Date.now()) {
            if (pendingForId) {
                clearTimeout(pendingForId.expiryTimer);
                pendingIncomingSecretsRef.current.delete(data.id);
            }
            setSecretMessageError("The sender's secure messaging key expired.");
            return;
        }
        // Remove before awaiting crypto so another relay copy cannot race this
        // queued replay through a second decrypt/render path.
        if (pendingForId) {
            clearTimeout(pendingForId.expiryTimer);
            pendingIncomingSecretsRef.current.delete(data.id);
        }
        incomingSecretDecryptsRef.current.set(data.id, seenBinding);
        const decryptContext = incomingSecretContextRef.current;
        const binding = {
            room,
            senderDeviceId: data.senderDeviceId,
            recipientDeviceId: data.recipientDeviceId,
            id: data.id,
            timestamp: data.timestamp,
        };
        try {
            const text = await decryptSecretMessage(data.encryptedPayload, pair.privateKey, peer.publicKey, binding);
            // Re-check identity after async crypto: disconnect/room/device changes
            // clear trust, and plaintext from the old context must not be installed.
            if (walletAddressRef.current?.toLowerCase() !== room ||
                data.recipientDeviceId !== deviceIdRef.current ||
                incomingSecretContextRef.current !== decryptContext ||
                secretPeerKeysRef.current.get(data.senderDeviceId) !== peer)
                return;
            seenSecretIdsRef.current.set(data.id, seenBinding);
            const direct = {
                ...binding, text, peerDeviceId: data.senderDeviceId, isMine: false,
            };
            setSecretMessages((current) => ({
                ...current,
                [data.senderDeviceId]: [...(current[data.senderDeviceId] || []), direct].slice(-200),
            }));
            const ackWs = wsRef.current;
            if (ackWs?.readyState === WebSocket.OPEN) {
                ackWs.send(JSON.stringify({
                    type: "secret_message_ack", room, senderDeviceId: data.senderDeviceId,
                    recipientDeviceId: deviceIdRef.current, id: data.id, digest: data.digest,
                }));
            }
            setSecretMessageError(null);
        }
        catch {
            setSecretMessageError("A secret message failed authentication and was rejected.");
        }
        finally {
            if (incomingSecretDecryptsRef.current.get(data.id) === seenBinding) {
                incomingSecretDecryptsRef.current.delete(data.id);
            }
        }
    }, []);
    processIncomingSecretRef.current = processIncomingSecret;
    const drainPendingIncomingSecrets = useCallback((senderDeviceId) => {
        const peer = secretPeerKeysRef.current.get(senderDeviceId);
        if (!peer || (!peer.walletSigned && !peer.safetyConfirmed))
            return;
        const pending = [...pendingIncomingSecretsRef.current.values()]
            .filter((entry) => entry.senderDeviceId === senderDeviceId);
        pending.forEach((entry) => {
            void processIncomingSecretRef.current?.(entry.data, true);
        });
    }, []);
    const advertiseSecretKey = useCallback(() => {
        const ws = wsRef.current;
        const pair = secretKeyPairRef.current;
        const room = walletAddressRef.current?.trim().toLowerCase();
        if (!room ||
            !pair ||
            !ws ||
            ws.readyState !== WebSocket.OPEN ||
            secretAdvertisementInFlightRef.current ||
            secretAdvertisementSigningBlockedRef.current)
            return;
        // Guests are authenticated by their room-scoped JWT. Token-less wallet
        // sockets must first complete wallet_auth; the relay enforces this too.
        if (!guestTokenRef.current && !deviceSessionTokenRef.current && !walletAuthAckedRef.current)
            return;
        const advertisementContext = secretAdvertisementContextRef.current;
        const isCurrentContext = () => secretAdvertisementContextRef.current === advertisementContext &&
            walletAddressRef.current?.trim().toLowerCase() === room &&
            secretKeyPairRef.current === pair;
        secretAdvertisementInFlightRef.current = true;
        void (async () => {
            let advertisement = localSecretAdvertisementRef.current;
            const canReuse = advertisement &&
                advertisement.room === room &&
                advertisement.deviceId === deviceIdRef.current &&
                advertisement.expiresAt > Date.now() + 60_000;
            if (!canReuse) {
                if (advertisement) {
                    clearSecretConversationState();
                    recordSecretDiagnostic({
                        kind: 'readiness_invalidated',
                        reason: 'local_key_rotation',
                    });
                }
                const fingerprint = await publicKeyFingerprint(pair.publicJwk);
                if (!isCurrentContext())
                    return;
                const unsigned = {
                    version: 1, room, deviceId: deviceIdRef.current, publicKey: pair.publicJwk,
                    fingerprint, epoch: crypto.randomUUID(), expiresAt: Date.now() + 10 * 60 * 1000,
                };
                // A current relay can attest an unsigned ephemeral key after the
                // socket's wallet_auth proof. Older relays require the exact direct-key
                // signature, so keep that fallback — but never ask for it repeatedly
                // after a rejection or reconnect.
                const relayCanAttest = !!guestTokenRef.current ||
                    !!deviceSessionTokenRef.current ||
                    directKeyRelayAttestationRef.current;
                const signer = relayCanAttest ? null : signWalletAuthRef.current;
                let signature;
                if (signer) {
                    try {
                        signature = await signer(directKeyAdvertisementMessage(unsigned));
                        if (!isCurrentContext())
                            return;
                    }
                    catch (error) {
                        if (isCurrentContext())
                            secretAdvertisementSigningBlockedRef.current = true;
                        throw error;
                    }
                }
                advertisement = { ...unsigned, ...(signature ? { signature } : {}) };
                if (!isCurrentContext())
                    return;
                localSecretAdvertisementRef.current = advertisement;
            }
            if (!advertisement)
                return;
            if (!isCurrentContext() || wsRef.current !== ws || ws.readyState !== WebSocket.OPEN)
                return;
            ws.send(JSON.stringify({ type: "secret_key_advertisement", ...advertisement }));
            secretKeyRegisteredRef.current = false;
            if (secretAdvertisementRefreshTimerRef.current)
                clearTimeout(secretAdvertisementRefreshTimerRef.current);
            secretAdvertisementRefreshTimerRef.current = setTimeout(advertiseSecretKey, Math.max(1_000, advertisement.expiresAt - Date.now() - 60_000));
        })()
            .catch(() => {
            if (isCurrentContext())
                setSecretMessageError("Could not publish the secure messaging key.");
        })
            .finally(() => {
            if (isCurrentContext())
                secretAdvertisementInFlightRef.current = false;
        });
    }, [clearSecretConversationState, recordSecretDiagnostic]);
    const sendSecretConversationOpen = useCallback((deviceId) => {
        const ws = wsRef.current;
        const room = walletAddressRef.current?.trim().toLowerCase();
        if (!ws || ws.readyState !== WebSocket.OPEN || !room)
            return;
        ws.send(JSON.stringify({
            type: "secret_conversation_open",
            room,
            senderDeviceId: deviceIdRef.current,
            recipientDeviceId: deviceId,
        }));
        recordSecretDiagnostic({ kind: 'consent', transition: 'request_sent' });
    }, [recordSecretDiagnostic]);
    useEffect(() => {
        if (!normalizedRoom)
            return;
        let cancelled = false;
        const room = normalizedRoom;
        void generateSecretDeviceKeyPair()
            .then((pair) => {
            if (cancelled || walletAddressRef.current?.trim().toLowerCase() !== room)
                return;
            secretKeyPairRef.current = pair;
            advertiseSecretKey();
        })
            .catch((err) => {
            console.warn("[useChatWebSocket] Direct-message ECDH unavailable:", err);
            setSecretMessageError("Secure direct messaging is not supported by this browser.");
        });
        return () => {
            cancelled = true;
            secretKeyPairRef.current = null;
            localSecretAdvertisementRef.current = null;
            secretAdvertisementInFlightRef.current = false;
            secretAdvertisementSigningBlockedRef.current = false;
            secretPeerKeysRef.current.clear();
            secretPeerExpiryTimersRef.current.forEach(clearTimeout);
            secretPeerExpiryTimersRef.current.clear();
            if (secretAdvertisementRefreshTimerRef.current)
                clearTimeout(secretAdvertisementRefreshTimerRef.current);
            secretAdvertisementRefreshTimerRef.current = null;
            seenSecretIdsRef.current.clear();
            clearPendingIncomingSecrets();
            pendingSecretSendsRef.current.forEach(({ reject, deliveryTimer, retryTimer }) => {
                clearTimeout(deliveryTimer);
                if (retryTimer)
                    clearTimeout(retryTimer);
                reject(new Error("Secret message connection closed."));
            });
            pendingSecretSendsRef.current.clear();
        };
    }, [
        normalizedRoom,
        guestToken,
        deviceSessionToken,
        suppliedDeviceId,
        advertiseSecretKey,
        clearPendingIncomingSecrets,
    ]);
    const prevWalletRef = useRef(normalizedRoom);
    const diagnosticsScopeInitializedRef = useRef(false);
    const diagnosticsAuthInitializedRef = useRef(false);
    // Keep exported diagnostics scoped to one room/device identity. These
    // effects intentionally retain no token or identifier; dependency changes
    // are enough to discard the previous bounded buffer.
    useLayoutEffect(() => {
        if (diagnosticsScopeInitializedRef.current) {
            clearSecretStateForAuthBoundary();
            rescopeSecretDiagnostics('scope_reset');
        }
        else {
            diagnosticsScopeInitializedRef.current = true;
        }
    }, [
        clearSecretStateForAuthBoundary,
        normalizedRoom,
        rescopeSecretDiagnostics,
        suppliedDeviceId,
    ]);
    useLayoutEffect(() => {
        if (diagnosticsAuthInitializedRef.current) {
            clearSecretStateForAuthBoundary();
            rescopeSecretDiagnostics('auth_reset');
        }
        else {
            diagnosticsAuthInitializedRef.current = true;
        }
    }, [
        clearSecretStateForAuthBoundary,
        deviceSessionToken,
        guestToken,
        rescopeSecretDiagnostics,
    ]);
    const attemptAdminPresenceAuthorization = useCallback(() => {
        const ws = wsRef.current;
        const challenge = adminPresenceChallengeRef.current;
        const room = walletAddressRef.current;
        if (guestTokenRef.current ||
            deviceSessionTokenRef.current ||
            !walletAuthAckedRef.current ||
            !adminPresenceActiveRef.current ||
            !ws ||
            ws.readyState !== WebSocket.OPEN ||
            !challenge ||
            adminPresenceSigningRef.current ||
            adminPresenceLegacyAttemptedRef.current ||
            adminPresenceAttemptedChallengeRef.current === challenge) {
            return;
        }
        const signer = signWalletAuthRef.current;
        if (!signer || !room)
            return;
        adminPresenceSigningRef.current = true;
        adminPresenceLegacyAttemptedRef.current = true;
        adminPresenceAttemptedChallengeRef.current = challenge;
        const canonicalMessage = buildAdminPresenceAuthMessage(room, challenge);
        void signer(canonicalMessage)
            .then((signature) => {
            // The challenge is one-use and socket-bound. Do not send an auth
            // acquired for an old socket/challenge, nor after the UI was closed.
            if (wsRef.current !== ws ||
                ws.readyState !== WebSocket.OPEN ||
                guestTokenRef.current ||
                !walletAuthAckedRef.current ||
                !adminPresenceActiveRef.current ||
                adminPresenceChallengeRef.current !== challenge ||
                walletAddressRef.current !== room) {
                return;
            }
            ws.send(JSON.stringify({
                type: 'admin_presence',
                active: true,
                auth: { challenge, signature },
            }));
        })
            .catch(() => {
            // User rejection is expected and deliberately remains inactive. The
            // attempted challenge ref prevents automatic re-prompting.
            console.warn('[useChatWebSocket] Admin presence authorization was not signed');
        })
            .finally(() => {
            if (wsRef.current === ws &&
                adminPresenceChallengeRef.current === challenge &&
                walletAddressRef.current === room) {
                adminPresenceSigningRef.current = false;
            }
        });
    }, []);
    const sendAdminPresence = useCallback(() => {
        // Clear local authorization state before checking socket/authentication
        // readiness. Closing the UI while disconnected or pre-auth must reset the
        // next false -> true activation, while nulling the challenge prevents a
        // stale in-flight signature from reviving presence.
        if (!adminPresenceActiveRef.current) {
            adminPresenceAckedRef.current = false;
            if (adminPresenceRetryRef.current) {
                clearInterval(adminPresenceRetryRef.current);
                adminPresenceRetryRef.current = null;
            }
            adminPresenceChallengeRef.current = null;
            adminPresenceAttemptedChallengeRef.current = null;
            adminPresenceLegacyAttemptedRef.current = false;
            adminPresenceSigningRef.current = false;
        }
        const ws = wsRef.current;
        // Presence is meaningful only for a socket that authenticated as the
        // wallet owner; guest connections must never send this control message.
        if (guestTokenRef.current ||
            !walletAuthAckedRef.current ||
            !ws ||
            ws.readyState !== WebSocket.OPEN) {
            return;
        }
        if (adminPresenceActiveRef.current) {
            if (deviceSessionTokenRef.current) {
                ws.send(JSON.stringify({ type: 'admin_presence', active: true }));
                return;
            }
            attemptAdminPresenceAuthorization();
            return;
        }
        ws.send(JSON.stringify({
            type: 'admin_presence',
            active: false,
        }));
    }, [attemptAdminPresenceAuthorization]);
    // Do not put this desired state in the connection effect dependencies:
    // navigating away from mobile chat must keep its provider socket/call engine
    // alive while updating only the verified socket's presence signal.
    useEffect(() => {
        sendAdminPresence();
    }, [adminPresenceActive, sendAdminPresence]);
    // Messages that arrived before the E2E key was available (guest joining
    // mid-handoff): retried periodically until decryptable, bounded lifetime.
    const pendingDecryptRef = useRef([]);
    const pendingTimerRef = useRef(null);
    const PENDING_RETRY_MS = 3000;
    const PENDING_MAX_AGE_MS = 5 * 60 * 1000;
    const drainPendingMessages = useCallback(async () => {
        const generation = commonGenerationRef.current;
        const room = walletAddressRef.current;
        const socket = wsRef.current;
        const pending = pendingDecryptRef.current;
        if (pending.length === 0) {
            if (pendingTimerRef.current) {
                clearInterval(pendingTimerRef.current);
                pendingTimerRef.current = null;
            }
            return;
        }
        const still = [];
        const decoded = [];
        for (const entry of pending) {
            try {
                const text = await decryptFnRef.current(entry.data.encryptedPayload);
                decoded.push({
                    generation: entry.data.generation ?? 0,
                    id: entry.data.id || `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    text,
                    encryptedPayload: entry.data.encryptedPayload,
                    deviceId: entry.data.deviceId,
                    timestamp: entry.data.timestamp || Date.now(),
                    isMine: false,
                });
            }
            catch {
                if (Date.now() - entry.queuedAt < PENDING_MAX_AGE_MS) {
                    still.push(entry);
                }
                else {
                    // Expired without a key: surface a visible placeholder instead of
                    // dropping the message silently. Stable id (server id preferred)
                    // keeps the notice from duplicating across reconnects.
                    console.warn('[useChatWebSocket] Giving up on undecryptable message — showing notice');
                    decoded.push({
                        generation: entry.data.generation ?? 0,
                        id: entry.data.id || `failed-${entry.data.timestamp || entry.queuedAt}-${entry.data.deviceId || 'unknown'}`,
                        text: '',
                        deviceId: entry.data.deviceId,
                        timestamp: entry.data.timestamp || entry.queuedAt,
                        isMine: false,
                        failedDecrypt: true,
                    });
                }
            }
        }
        if (generation !== commonGenerationRef.current || room !== walletAddressRef.current || socket !== wsRef.current)
            return;
        pendingDecryptRef.current = still.filter((entry) => (entry.data.generation ?? 0) === generation);
        if (decoded.length > 0) {
            console.log('[useChatWebSocket] Decrypted', decoded.length, 'queued message(s) after key arrival');
            setMessages((prev) => room !== walletAddressRef.current || socket !== wsRef.current ? prev :
                [...prev, ...decoded.filter((d) => d.generation === commonGenerationRef.current && !prev.some((m) => m.id === d.id))]
                    .sort((a, b) => a.timestamp - b.timestamp));
        }
        if (still.length === 0 && pendingTimerRef.current) {
            clearInterval(pendingTimerRef.current);
            pendingTimerRef.current = null;
        }
    }, []);
    const queuePendingMessage = useCallback((data) => {
        if ((data.generation ?? 0) !== commonGenerationRef.current)
            return;
        console.warn('[useChatWebSocket] E2E key not yet available — queuing message for retry');
        pendingDecryptRef.current.push({ data, queuedAt: Date.now() });
        if (!pendingTimerRef.current) {
            pendingTimerRef.current = setInterval(() => {
                void drainPendingMessages();
            }, PENDING_RETRY_MS);
        }
    }, [drainPendingMessages]);
    useEffect(() => () => {
        if (pendingTimerRef.current) {
            clearInterval(pendingTimerRef.current);
            pendingTimerRef.current = null;
        }
        pendingDecryptRef.current = [];
    }, []);
    useEffect(() => {
        if (normalizedRoom !== prevWalletRef.current) {
            prevWalletRef.current = normalizedRoom;
            commonGenerationRef.current = null;
            rejectCommonRequests('chat_room_changed');
            pendingDecryptRef.current = [];
            if (pendingTimerRef.current)
                clearInterval(pendingTimerRef.current);
            pendingTimerRef.current = null;
            setMessages([]);
            // Room change: a scheduler notice (and its dedupe key) belongs to the
            // previous room — never show it in the new one.
            setSystemNotice(null);
            lastNoticeKeyRef.current = null;
            // Room change: queued moderation signals belong to the previous room.
            pendingModerationRef.current = [];
            setSecretMessages({});
            secretPeerKeysRef.current.clear();
            seenSecretIdsRef.current.clear();
            clearSecretConversationState();
            clearPendingIncomingSecrets();
            setSecretMessageError(null);
            localSecretAdvertisementRef.current = null;
            secretAdvertisementSigningBlockedRef.current = false;
            directKeyRelayAttestationRef.current = !!deviceSessionTokenRef.current;
            setGuestAccessClosed(false);
            setOrganizerPresent(false);
            guestAccessClosedRef.current = false;
            // A room has its own socket-bound challenge and its own UI activation.
            // Do this on room changes even if the new socket is not open yet.
            adminPresenceChallengeRef.current = null;
            adminPresenceAttemptedChallengeRef.current = null;
            adminPresenceLegacyAttemptedRef.current = false;
            adminPresenceSigningRef.current = false;
        }
    }, [normalizedRoom, clearPendingIncomingSecrets, clearSecretConversationState]);
    useEffect(() => {
        if (commonGenerationRef.current !== null)
            persistMessages(walletAddress, messages);
    }, [messages, walletAddress]);
    useEffect(() => {
        if (!normalizedRoom) {
            mountedRef.current = false;
            return () => { mountedRef.current = false; };
        }
        mountedRef.current = true;
        const normalizedAddress = normalizedRoom;
        const cleanup = () => {
            rejectCommonRequests('chat_session_changed');
            pendingDecryptRef.current = [];
            if (pendingTimerRef.current)
                clearInterval(pendingTimerRef.current);
            pendingTimerRef.current = null;
            clearPendingIncomingSecrets();
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            if (pingTimerRef.current) {
                clearInterval(pingTimerRef.current);
                pingTimerRef.current = null;
            }
            if (roomConfigTimerRef.current) {
                clearTimeout(roomConfigTimerRef.current);
                roomConfigTimerRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.onclose = null;
                wsRef.current.onerror = null;
                wsRef.current.onmessage = null;
                wsRef.current.close();
                wsRef.current = null;
            }
            if (adminPresenceRetryRef.current) {
                clearInterval(adminPresenceRetryRef.current);
                adminPresenceRetryRef.current = null;
            }
        };
        const connect = () => {
            if (!mountedRef.current)
                return;
            cleanup();
            // CHAT_WS_URL lets the chat/call relay target differ from the upload
            // orchestrator (e.g. use the local relay while the deployed one is older).
            const orchestratorUrl = process.env.CHAT_WS_URL || process.env.ORCHESTRATOR_URL || '/orchestrator';
            let url;
            if (orchestratorUrl.startsWith('http')) {
                url = `${orchestratorUrl.replace(/^http/, 'ws')}/ws/chat/${normalizedAddress}`;
            }
            else {
                const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                url = `${wsProtocol}//${window.location.host}${orchestratorUrl}/ws/chat/${normalizedAddress}`;
            }
            const params = new URLSearchParams();
            params.set('device_id', deviceIdRef.current);
            if (guestToken)
                params.set('guest_token', guestToken);
            url += `?${params.toString()}`;
            // The query string can carry a guest JWT; never put it in console logs.
            console.log('[useChatWebSocket] Connecting to', url.split('?')[0]);
            const protocols = deviceSessionToken
                ? [`dwinity-device-session.${deviceSessionToken}`]
                : undefined;
            const ws = protocols ? new WebSocket(url, protocols) : new WebSocket(url);
            wsRef.current = ws;
            const socketGeneration = ++socketGenerationRef.current;
            ws.onopen = () => {
                if (!mountedRef.current || wsRef.current !== ws || socketGenerationRef.current !== socketGeneration)
                    return;
                commonGenerationRef.current = null;
                console.log('[useChatWebSocket] Connected');
                recordSecretDiagnostic({ kind: 'socket', event: 'connected' });
                setIsConnected(true);
                ws.send(JSON.stringify({
                    type: 'register',
                    deviceId: deviceIdRef.current,
                }));
                secretKeyRegisteredRef.current = false;
                if (guestToken || deviceSessionToken)
                    advertiseSecretKey();
                // Wallet-socket handshake: prove room-key possession so the relay
                // relays moderation signals (mute-all, kick) from this socket. Uses a
                // cached signature when available, so this rarely triggers signing.
                // Guests never do this; failure is non-fatal (chat/calls still work).
                // A single attempt is NOT enough (live incident 2026-08-12: signing
                // hung while the wallet was still initializing, the socket stayed
                // unverified forever and every mute was silently dropped) — so we
                // retry until the relay acks with `wallet_auth_ok`. The retry timer
                // also covers a signer that only becomes available after connect.
                walletAuthAckedRef.current = !!deviceSessionToken;
                adminPresenceAckedRef.current = false;
                directKeyRelayAttestationRef.current = !!deviceSessionTokenRef.current;
                walletAuthInFlightRef.current = false;
                adminPresenceChallengeRef.current = null;
                adminPresenceSigningRef.current = false;
                adminPresenceAttemptedChallengeRef.current = null;
                const attemptWalletAuth = () => {
                    if (guestToken || deviceSessionToken)
                        return;
                    if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN)
                        return;
                    if (walletAuthAckedRef.current || walletAuthInFlightRef.current)
                        return;
                    const signer = signWalletAuthRef.current;
                    if (!signer)
                        return; // not ready yet — the retry timer tries again
                    walletAuthInFlightRef.current = true;
                    void getWalletSocketAuth(normalizedAddress, signer)
                        .then((auth) => {
                        if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({ type: 'wallet_auth', auth }));
                        }
                    })
                        .catch((err) => {
                        console.warn('[useChatWebSocket] Wallet-socket auth signing failed (will retry):', err);
                    })
                        .finally(() => {
                        if (wsRef.current === ws &&
                            socketGenerationRef.current === socketGeneration &&
                            walletAddressRef.current?.trim().toLowerCase() === normalizedAddress) {
                            walletAuthInFlightRef.current = false;
                        }
                    });
                };
                walletAuthAttemptRef.current = attemptWalletAuth;
                if (!guestToken && !deviceSessionToken) {
                    attemptWalletAuth();
                    walletAuthTimerRef.current = setInterval(() => {
                        if (walletAuthAckedRef.current) {
                            if (walletAuthTimerRef.current) {
                                clearInterval(walletAuthTimerRef.current);
                                walletAuthTimerRef.current = null;
                            }
                            return;
                        }
                        attemptWalletAuth();
                    }, 15000);
                }
                if (deviceSessionToken && adminPresenceActiveRef.current) {
                    sendAdminPresence();
                    adminPresenceRetryRef.current = setInterval(() => {
                        if (wsRef.current !== ws ||
                            socketGenerationRef.current !== socketGeneration ||
                            ws.readyState !== WebSocket.OPEN ||
                            !adminPresenceActiveRef.current ||
                            adminPresenceAckedRef.current ||
                            deviceSessionTokenRef.current !== deviceSessionToken ||
                            walletAddressRef.current?.trim().toLowerCase() !== normalizedAddress) {
                            if (adminPresenceRetryRef.current)
                                clearInterval(adminPresenceRetryRef.current);
                            adminPresenceRetryRef.current = null;
                            return;
                        }
                        sendAdminPresence();
                    }, 2_000);
                }
                pingTimerRef.current = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ type: 'ping' }));
                    }
                }, 25000);
                // Old relay deployments never send `room_config`. If none arrives
                // shortly after connecting, flag the archive state as unknown so the
                // UI can warn instead of silently assuming "archive off".
                if (!roomConfigReceivedRef.current) {
                    roomConfigTimerRef.current = setTimeout(() => {
                        if (mountedRef.current &&
                            wsRef.current === ws &&
                            socketGenerationRef.current === socketGeneration &&
                            !roomConfigReceivedRef.current) {
                            console.warn('[useChatWebSocket] No room_config from relay — legacy relay? Archive state unknown.');
                            setArchiveStateUnknown(true);
                        }
                    }, 5000);
                }
            };
            ws.onmessage = async (event) => {
                if (!mountedRef.current || wsRef.current !== ws || socketGenerationRef.current !== socketGeneration)
                    return;
                try {
                    const data = JSON.parse(event.data);
                    switch (data.type) {
                        case 'connected':
                            commonAckVersionRef.current = data.commonChatAckVersion === 1;
                            if (Number.isSafeInteger(data.chatGeneration) && data.chatGeneration >= 0) {
                                commonGenerationRef.current = data.chatGeneration;
                                const cached = loadPersistedMessages(walletAddressRef.current)
                                    .filter((m) => (m.generation ?? 0) === data.chatGeneration);
                                setMessages((prev) => {
                                    if (wsRef.current !== ws || walletAddressRef.current?.toLowerCase() !== normalizedAddress)
                                        return prev;
                                    const kept = prev.filter((m) => (m.generation ?? 0) === commonGenerationRef.current);
                                    return [...kept, ...cached.filter((m) => !kept.some((p) => p.id === m.id))];
                                });
                                pendingDecryptRef.current = pendingDecryptRef.current
                                    .filter((entry) => (entry.data.generation ?? 0) === data.chatGeneration);
                            }
                            console.log('[useChatWebSocket] Connected with', data.deviceCount, 'device(s)');
                            setConnectedDevices(data.deviceCount ?? 1);
                            if (typeof data.organizerPresent === 'boolean')
                                setOrganizerPresent(data.organizerPresent);
                            if (deviceSessionTokenRef.current)
                                sendAdminPresence();
                            break;
                        case 'device_joined':
                            console.log('[useChatWebSocket] Device joined, now', data.deviceCount, 'device(s)');
                            if (data.deviceCount != null) {
                                setConnectedDevices(data.deviceCount);
                            }
                            else {
                                setConnectedDevices((prev) => prev + 1);
                            }
                            break;
                        case 'device_left':
                            console.log('[useChatWebSocket] Device left, now', data.deviceCount, 'device(s)');
                            if (data.deviceCount != null) {
                                setConnectedDevices(data.deviceCount);
                            }
                            else {
                                setConnectedDevices((prev) => Math.max(0, prev - 1));
                            }
                            break;
                        case 'room_config':
                            if (typeof data.archiveEnabled === 'boolean') {
                                roomConfigReceivedRef.current = true;
                                if (roomConfigTimerRef.current) {
                                    clearTimeout(roomConfigTimerRef.current);
                                    roomConfigTimerRef.current = null;
                                }
                                setArchiveStateUnknown(false);
                                setArchiveEnabledState(data.archiveEnabled);
                            }
                            break;
                        case 'presence':
                            if (Array.isArray(data.devices)) {
                                const devices = data.devices.filter((d) => d && typeof d.deviceId === 'string');
                                setRoomDevices(devices);
                                if (typeof data.organizerPresent === 'boolean') {
                                    setOrganizerPresent(data.organizerPresent);
                                }
                                else {
                                    // Older relay compatibility: only an explicit verification
                                    // bit is proof. `kind: wallet` alone is attacker-controlled.
                                    setOrganizerPresent(devices.some((d) => d.walletVerified === true));
                                }
                                if (deviceSessionTokenRef.current &&
                                    adminPresenceActiveRef.current &&
                                    !adminPresenceAckedRef.current)
                                    sendAdminPresence();
                                // Advertisements are validated independently below. Presence
                                // is deliberately not treated as key identity evidence.
                            }
                            break;
                        case "secret_key_advertisement":
                            if (typeof data.deviceId === "string" &&
                                data.deviceId !== deviceIdRef.current &&
                                data.version === 1 &&
                                data.room === walletAddressRef.current?.toLowerCase() &&
                                data.publicKey && typeof data.publicKey === "object" &&
                                typeof data.fingerprint === "string" &&
                                typeof data.epoch === "string" &&
                                typeof data.expiresAt === "number" &&
                                data.expiresAt > Date.now() && data.expiresAt <= Date.now() + 11 * 60 * 1000) {
                                const advertisementSocket = ws;
                                const advertisementRoom = data.room;
                                const advertisementDeviceId = data.deviceId;
                                const advertisementLocalDeviceId = deviceIdRef.current;
                                const advertisementContext = secretAdvertisementContextRef.current;
                                const advertisementIdentity = JSON.stringify([
                                    data.epoch, data.fingerprint, data.publicKey,
                                ]);
                                const priorAdvertisement = latestSecretAdvertisementsRef.current.get(data.deviceId);
                                const advertisementGeneration = (priorAdvertisement?.generation || 0) + 1;
                                latestSecretAdvertisementsRef.current.set(data.deviceId, {
                                    generation: advertisementGeneration,
                                    identity: advertisementIdentity,
                                    epoch: data.epoch,
                                    fingerprint: data.fingerprint,
                                });
                                recordSecretDiagnostic({ kind: 'advertisement', event: 'observed' });
                                // Do not decrypt new ciphertext with a superseded peer key
                                // while its replacement is still being fingerprinted.
                                const installedPeerBeforeAdvertisement = secretPeerKeysRef.current.get(data.deviceId);
                                const installedPeerIdentity = installedPeerBeforeAdvertisement && JSON.stringify([
                                    installedPeerBeforeAdvertisement.epoch,
                                    installedPeerBeforeAdvertisement.fingerprint,
                                    installedPeerBeforeAdvertisement.publicKey,
                                ]);
                                const installedPeerExpired = !!installedPeerBeforeAdvertisement &&
                                    installedPeerBeforeAdvertisement.expiresAt <= Date.now();
                                const hasConsentState = pendingSecretApprovalsRef.current.has(data.deviceId) ||
                                    localSecretConversationOpenRef.current.has(data.deviceId) ||
                                    remoteSecretConversationOpenRef.current.has(data.deviceId) ||
                                    readySecretConversationsRef.current.has(data.deviceId);
                                const identityDiffers = priorAdvertisement
                                    ? priorAdvertisement.identity !== advertisementIdentity
                                    : installedPeerBeforeAdvertisement
                                        ? installedPeerIdentity !== advertisementIdentity
                                        : false;
                                // A reconnect clears the latest-advertisement observation map,
                                // but it does not necessarily clear an installed peer key. An
                                // exact, still-valid replay must therefore retain that key and
                                // its SAS/consent state. An absent prior observation plus a
                                // changed identity is still a rotation and must clear it.
                                const replacesInstalledState = identityDiffers || installedPeerExpired;
                                if (replacesInstalledState) {
                                    const epochChanged = priorAdvertisement
                                        ? priorAdvertisement.epoch !== data.epoch
                                        : installedPeerBeforeAdvertisement
                                            ? installedPeerBeforeAdvertisement.epoch !== data.epoch
                                            : false;
                                    const keyChanged = priorAdvertisement
                                        ? priorAdvertisement.fingerprint !== data.fingerprint ||
                                            (!epochChanged && priorAdvertisement.identity !== advertisementIdentity)
                                        : installedPeerBeforeAdvertisement
                                            ? installedPeerBeforeAdvertisement.fingerprint !== data.fingerprint ||
                                                (!epochChanged && installedPeerIdentity !== advertisementIdentity)
                                            : false;
                                    if (identityDiffers) {
                                        recordSecretDiagnostic({
                                            kind: 'advertisement',
                                            event: 'identity_changed',
                                            change: epochChanged && keyChanged ? 'both' : epochChanged ? 'epoch' : 'key',
                                        });
                                    }
                                    const hadInstalledPeer = secretPeerKeysRef.current.has(data.deviceId);
                                    const hadConsent = hasConsentState;
                                    if (hadInstalledPeer || hadConsent) {
                                        recordSecretDiagnostic({
                                            kind: 'readiness_invalidated',
                                            reason: installedPeerExpired
                                                ? 'peer_advertisement_expiry'
                                                : epochChanged
                                                    ? 'peer_advertisement_epoch'
                                                    : 'peer_advertisement_identity',
                                        });
                                    }
                                    if (hadConsent) {
                                        recordSecretDiagnostic({ kind: 'consent', transition: 'reset' });
                                        pendingSecretApprovalsRef.current.delete(data.deviceId);
                                        localSecretConversationOpenRef.current.delete(data.deviceId);
                                        remoteSecretConversationOpenRef.current.delete(data.deviceId);
                                        readySecretConversationsRef.current.delete(data.deviceId);
                                        setSecretConversationVersion((value) => value + 1);
                                    }
                                    if (hadInstalledPeer) {
                                        const priorExpiryTimer = secretPeerExpiryTimersRef.current.get(data.deviceId);
                                        if (priorExpiryTimer)
                                            clearTimeout(priorExpiryTimer);
                                        secretPeerExpiryTimersRef.current.delete(data.deviceId);
                                        if (secretPeerKeysRef.current.delete(data.deviceId)) {
                                            setSecretPeerVersion((value) => value + 1);
                                        }
                                    }
                                }
                                void (async () => {
                                    const actualFingerprint = await publicKeyFingerprint(data.publicKey);
                                    if (actualFingerprint !== data.fingerprint) {
                                        recordSecretDiagnostic({ kind: 'advertisement', event: 'rejected' });
                                        return;
                                    }
                                    let walletSigned = false;
                                    if (typeof data.signature === "string") {
                                        try {
                                            walletSigned = ethers.utils.verifyMessage(directKeyAdvertisementMessage({
                                                version: 1, room: data.room, deviceId: data.deviceId,
                                                publicKey: data.publicKey, fingerprint: data.fingerprint,
                                                epoch: data.epoch, expiresAt: data.expiresAt,
                                            }), data.signature).toLowerCase() === data.room;
                                        }
                                        catch {
                                            walletSigned = false;
                                        }
                                        // A present but invalid wallet signature is an attack, not
                                        // an unsigned guest advertisement; reject it outright.
                                        if (!walletSigned) {
                                            recordSecretDiagnostic({ kind: 'advertisement', event: 'rejected' });
                                            return;
                                        }
                                    }
                                    // Device-session owner sockets deliberately do not prompt
                                    // for an additional wallet signature.  The relay adds this
                                    // flag only after validating that session; do not infer it
                                    // from ordinary presence (which is attacker-controlled).
                                    if (!data.signature && data.relayAttested === true) {
                                        walletSigned = true;
                                    }
                                    const own = secretKeyPairRef.current;
                                    const ownFingerprint = own && await publicKeyFingerprint(own.publicJwk);
                                    const safetyNumber = own && ownFingerprint
                                        ? await deriveSafetyNumber(own.privateKey, data.publicKey, data.room, deviceIdRef.current, data.deviceId, ownFingerprint, data.fingerprint)
                                        : undefined;
                                    // Fingerprinting, signature recovery, and SAS derivation are
                                    // asynchronous. Never install their result into a replaced
                                    // socket, room, or local-device context.
                                    if (wsRef.current !== advertisementSocket ||
                                        walletAddressRef.current?.toLowerCase() !== advertisementRoom ||
                                        data.deviceId !== advertisementDeviceId ||
                                        deviceIdRef.current !== advertisementLocalDeviceId ||
                                        secretAdvertisementContextRef.current !== advertisementContext ||
                                        latestSecretAdvertisementsRef.current.get(data.deviceId)?.generation !== advertisementGeneration ||
                                        latestSecretAdvertisementsRef.current.get(data.deviceId)?.identity !== advertisementIdentity ||
                                        data.expiresAt <= Date.now())
                                        return;
                                    const installedPeer = secretPeerKeysRef.current.get(data.deviceId);
                                    const isExactInstalledIdentity = !!installedPeer &&
                                        installedPeer.epoch === data.epoch &&
                                        installedPeer.fingerprint === data.fingerprint &&
                                        JSON.stringify(installedPeer.publicKey) === JSON.stringify(data.publicKey);
                                    const userAlreadyApproved = pendingSecretApprovalsRef.current.delete(data.deviceId);
                                    let peerToInstall;
                                    if (isExactInstalledIdentity) {
                                        // An exact retransmission is idempotent. In particular, an
                                        // unsigned guest who already confirmed this exact key must
                                        // not be forced through SAS approval again. A newly valid
                                        // signed/attested copy may strengthen, never downgrade,
                                        // authentication for the same key.
                                        installedPeer.expiresAt = data.expiresAt;
                                        installedPeer.walletSigned = installedPeer.walletSigned || walletSigned;
                                        installedPeer.safetyConfirmed =
                                            installedPeer.safetyConfirmed || installedPeer.walletSigned || userAlreadyApproved;
                                        if (!installedPeer.safetyNumber && safetyNumber) {
                                            installedPeer.safetyNumber = safetyNumber;
                                        }
                                        peerToInstall = installedPeer;
                                    }
                                    else {
                                        peerToInstall = {
                                            publicKey: data.publicKey, fingerprint: data.fingerprint, epoch: data.epoch,
                                            expiresAt: data.expiresAt, walletSigned, safetyNumber,
                                            // Only a valid room-owner signature is automatic. Guests
                                            // must compare and explicitly confirm their SAS each epoch.
                                            safetyConfirmed: walletSigned || userAlreadyApproved,
                                        };
                                        secretPeerKeysRef.current.set(data.deviceId, peerToInstall);
                                    }
                                    const priorExpiryTimer = secretPeerExpiryTimersRef.current.get(data.deviceId);
                                    if (priorExpiryTimer)
                                        clearTimeout(priorExpiryTimer);
                                    const epoch = data.epoch;
                                    secretPeerExpiryTimersRef.current.set(data.deviceId, setTimeout(() => {
                                        const current = secretPeerKeysRef.current.get(data.deviceId);
                                        if (current?.epoch !== epoch)
                                            return;
                                        secretPeerKeysRef.current.delete(data.deviceId);
                                        secretPeerExpiryTimersRef.current.delete(data.deviceId);
                                        recordSecretDiagnostic({
                                            kind: 'readiness_invalidated',
                                            reason: 'peer_advertisement_expiry',
                                        });
                                        recordSecretDiagnostic({ kind: 'advertisement', event: 'expired' });
                                        setSecretPeerVersion((value) => value + 1);
                                    }, Math.max(0, data.expiresAt - Date.now())));
                                    // Exact duplicates retain object identity and trust. A
                                    // version update is still useful when their valid expiry was
                                    // refreshed or authentication was strengthened.
                                    setSecretPeerVersion((value) => value + 1);
                                    // Signed/relay-attested peers can decrypt immediately.
                                    // Unsigned guests remain queued while their SAS is visible.
                                    drainPendingIncomingSecrets(data.deviceId);
                                })().catch(() => { });
                            }
                            break;
                        case "secret_conversation_open":
                            if (data.room === walletAddressRef.current?.toLowerCase() &&
                                typeof data.senderDeviceId === "string" &&
                                data.senderDeviceId !== deviceIdRef.current &&
                                data.recipientDeviceId === deviceIdRef.current) {
                                remoteSecretConversationOpenRef.current.add(data.senderDeviceId);
                                recordSecretDiagnostic({ kind: 'consent', transition: 'request_received' });
                                setSecretConversationVersion((value) => value + 1);
                                setSecretMessageError(null);
                            }
                            break;
                        case "secret_conversation_ready":
                            if (data.room === walletAddressRef.current?.toLowerCase() &&
                                Array.isArray(data.deviceIds) &&
                                data.deviceIds.length === 2 &&
                                data.deviceIds.includes(deviceIdRef.current)) {
                                const peerDeviceId = data.deviceIds.find((candidate) => typeof candidate === "string" && candidate !== deviceIdRef.current);
                                if (typeof peerDeviceId === "string" &&
                                    localSecretConversationOpenRef.current.has(peerDeviceId) &&
                                    remoteSecretConversationOpenRef.current.has(peerDeviceId)) {
                                    readySecretConversationsRef.current.add(peerDeviceId);
                                    recordSecretDiagnostic({ kind: 'consent', transition: 'ready' });
                                    setSecretConversationVersion((value) => value + 1);
                                    setSecretMessageError(null);
                                    drainPendingIncomingSecrets(peerDeviceId);
                                }
                            }
                            break;
                        case "secret_message": {
                            await processIncomingSecret(data);
                            break;
                        }
                        case "secret_message_accepted":
                            // Relay accepted ciphertext; sender UI waits for the separate
                            // recipient AEAD-verified delivery acknowledgement.
                            {
                                const pending = pendingSecretSendsRef.current.get(data.id);
                                if (pending) {
                                    pending.accepted = true;
                                    if (!pending.retryTimer && pending.attempts < SECRET_ACK_MAX_ATTEMPTS) {
                                        pending.retryTimer = setTimeout(() => {
                                            pending.retryTimer = null;
                                            if (!pendingSecretSendsRef.current.has(data.id))
                                                return;
                                            retransmitPendingSecretSends(data.id);
                                        }, SECRET_ACK_RETRY_DELAY_MS);
                                    }
                                }
                            }
                            break;
                        case "secret_key_registered": {
                            const advertisement = localSecretAdvertisementRef.current;
                            if (data.deviceId === deviceIdRef.current &&
                                advertisement &&
                                data.epoch === advertisement.epoch) {
                                secretKeyRegisteredRef.current = true;
                                localSecretConversationOpenRef.current.forEach(sendSecretConversationOpen);
                                // Explicit relay confirmation, rather than a timing guess,
                                // proves this replacement socket can submit the old envelope.
                                retransmitPendingSecretSends();
                            }
                            break;
                        }
                        case "secret_message_delivered": {
                            const pending = pendingSecretSendsRef.current.get(data.id);
                            if (pending) {
                                clearTimeout(pending.deliveryTimer);
                                if (pending.retryTimer)
                                    clearTimeout(pending.retryTimer);
                                pendingSecretSendsRef.current.delete(data.id);
                                pending.resolve();
                            }
                            break;
                        }
                        case 'common_chat_cleared':
                            if (data.room && data.room !== normalizedAddress)
                                break;
                            if (!Number.isSafeInteger(data.generation) || data.generation < 0 ||
                                (commonGenerationRef.current !== null && data.generation < commonGenerationRef.current))
                                break;
                            commonGenerationRef.current = data.generation;
                            pendingDecryptRef.current = pendingDecryptRef.current.filter((entry) => entry.data.generation === data.generation);
                            setMessages((prev) => wsRef.current === ws && walletAddressRef.current?.toLowerCase() === normalizedAddress
                                ? prev.filter((m) => m.generation === commonGenerationRef.current) : prev);
                            if (walletAddressRef.current) {
                                persistMessages(walletAddressRef.current, loadPersistedMessages(walletAddressRef.current)
                                    .filter((m) => m.generation === data.generation));
                            }
                            for (const pending of commonSendRequestsRef.current.values()) {
                                if (pending.socket === ws && pending.room === normalizedAddress && pending.generation < data.generation) {
                                    pending.reject(new Error('chat_generation_stale'));
                                }
                            }
                            if (clearRequestRef.current && clearRequestRef.current.socket === ws &&
                                clearRequestRef.current.room === data.room && clearRequestRef.current.id === data.requestId &&
                                clearRequestRef.current.generation === data.requestGeneration &&
                                data.generation === clearRequestRef.current.generation + 1) {
                                clearRequestRef.current.resolve();
                                clearRequestRef.current = null;
                            }
                            break;
                        case 'common_chat_clear_error':
                            if (clearRequestRef.current?.socket === ws && clearRequestRef.current.room === data.room &&
                                clearRequestRef.current.id === data.requestId && clearRequestRef.current.generation === data.requestGeneration) {
                                clearRequestRef.current.reject(new Error(data.error || 'clear_failed'));
                            }
                            break;
                        case 'chat_message_accepted': {
                            const pending = commonSendRequestsRef.current.get(data.id);
                            if (pending?.socket === ws && pending.room === data.room && pending.generation === data.generation) {
                                if (pending.generation === commonGenerationRef.current)
                                    pending.resolve();
                                else
                                    pending.reject(new Error('chat_generation_stale'));
                            }
                            break;
                        }
                        case 'chat_message':
                            if (data.deviceId === deviceIdRef.current)
                                break;
                            if ((data.generation ?? 0) !== commonGenerationRef.current)
                                break;
                            try {
                                const decryptedText = await decryptFnRef.current(data.encryptedPayload);
                                if (wsRef.current !== ws || (data.generation ?? 0) !== commonGenerationRef.current)
                                    break;
                                const msg = {
                                    generation: data.generation ?? 0,
                                    id: data.id || `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                                    text: decryptedText,
                                    encryptedPayload: data.encryptedPayload,
                                    deviceId: data.deviceId,
                                    timestamp: data.timestamp || Date.now(),
                                    isMine: false,
                                };
                                setMessages((prev) => msg.generation === commonGenerationRef.current && wsRef.current === ws &&
                                    walletAddressRef.current?.toLowerCase() === normalizedAddress ? [...prev, msg] : prev);
                            }
                            catch (err) {
                                if (wsRef.current !== ws || walletAddressRef.current?.toLowerCase() !== normalizedAddress)
                                    break;
                                if (err instanceof ChatKeyUnavailableError || err?.name === 'ChatKeyUnavailableError') {
                                    // Key not held yet (e.g. guest waiting for the room-key
                                    // handoff) — queue and retry once the key arrives instead
                                    // of losing the message.
                                    queuePendingMessage(data);
                                }
                                else {
                                    console.error('[useChatWebSocket] Failed to decrypt message:', err);
                                }
                            }
                            break;
                        case 'call_signal':
                            if (data.from === deviceIdRef.current)
                                break;
                            if (data.to && data.to !== deviceIdRef.current)
                                break;
                            try {
                                onCallSignalRef.current?.({
                                    subtype: data.subtype,
                                    callId: data.callId,
                                    from: data.from,
                                    to: data.to,
                                    payload: data.payload,
                                    timestamp: data.timestamp,
                                    senderWalletVerified: data.senderWalletVerified === true,
                                });
                            }
                            catch (err) {
                                console.error('[useChatWebSocket] Call signal handler error:', err);
                            }
                            break;
                        case 'wallet_auth_ok': {
                            console.log('[useChatWebSocket] Wallet socket verified — moderation enabled');
                            walletAuthAckedRef.current = true;
                            directKeyRelayAttestationRef.current =
                                !!deviceSessionTokenRef.current ||
                                    data.directKeyRelayAttestation === true;
                            if (walletAuthTimerRef.current) {
                                clearInterval(walletAuthTimerRef.current);
                                walletAuthTimerRef.current = null;
                            }
                            // Flush moderation signals that were held back while the
                            // socket was unverified (fresh ones only — a 30s-old mute
                            // request is stale, not worth replaying).
                            const pending = pendingModerationRef.current;
                            pendingModerationRef.current = [];
                            const now = Date.now();
                            pending.forEach(({ signal, queuedAt }) => {
                                if (now - queuedAt > PENDING_MODERATION_MAX_AGE_MS) {
                                    console.warn(`[useChatWebSocket] Dropping stale queued moderation signal ${signal.subtype}`);
                                    return;
                                }
                                console.log(`[useChatWebSocket] Sending queued moderation signal ${signal.subtype}`);
                                sendCallSignalRef.current?.(signal);
                            });
                            // Authentication is not itself presence. True always requires
                            // the relay's fresh, socket-bound challenge proof below.
                            sendAdminPresence();
                            advertiseSecretKey();
                            break;
                        }
                        case 'admin_presence_ok':
                            if (typeof data.organizerPresent === 'boolean')
                                setOrganizerPresent(data.organizerPresent);
                            if (data.active === adminPresenceActiveRef.current) {
                                adminPresenceAckedRef.current = true;
                                if (adminPresenceRetryRef.current) {
                                    clearInterval(adminPresenceRetryRef.current);
                                    adminPresenceRetryRef.current = null;
                                }
                                if (data.active)
                                    advertiseSecretKey();
                            }
                            break;
                        case 'admin_presence_challenge':
                            if (!guestTokenRef.current &&
                                typeof data.challenge === 'string' &&
                                data.challenge) {
                                adminPresenceChallengeRef.current = data.challenge;
                                adminPresenceSigningRef.current = false;
                                adminPresenceAttemptedChallengeRef.current = null;
                                attemptAdminPresenceAuthorization();
                            }
                            break;
                        case 'error':
                            {
                                const pending = commonSendRequestsRef.current.get(data.id);
                                if (pending?.socket === ws && pending.room === data.room &&
                                    pending.generation === data.requestGeneration)
                                    pending.reject(new Error(data.error || 'chat_message_rejected'));
                            }
                            if (data.error === 'device_session_invalid' || data.error === 'device_session_revoked') {
                                clearSecretStateForAuthBoundary();
                                rescopeSecretDiagnostics('auth_reset');
                                onDeviceSessionRejectedRef.current?.();
                            }
                            else if (data.error === "admin_absent" && guestTokenRef.current) {
                                // Permanent guest links close when the verified host leaves.
                                // This is terminal for this session: do not reconnect-loop.
                                setGuestAccessClosed(true);
                                guestAccessClosedRef.current = true;
                                setSecretMessageError("The permanent invite is closed because its host is no longer present.");
                                try {
                                    ws.close(4001, "admin_absent");
                                }
                                catch { /* closed */ }
                            }
                            else if (data.error === 'wallet_auth_failed') {
                                // The relay rejected our proof (expired/stale signature) —
                                // drop the cache so the next attempt signs a fresh message.
                                console.warn('[useChatWebSocket] wallet_auth rejected — clearing cached proof and retrying');
                                clearWalletSocketAuth(normalizedAddress);
                                walletAuthAttemptRef.current?.();
                            }
                            else if (data.error === 'moderation_forbidden') {
                                // The relay dropped a moderation signal from this socket
                                // (unverified). Surface it and re-run the auth handshake —
                                // the caller-side queue prevents this in normal operation,
                                // so hitting it means the client/relay auth views diverged.
                                console.warn(`[useChatWebSocket] Relay dropped moderation signal ${data.subtype || ''} — socket not verified; re-authenticating`);
                                walletAuthAckedRef.current = false;
                                walletAuthAttemptRef.current?.();
                            }
                            else if (typeof data.error === "string" && data.error.startsWith("secret_")) {
                                const messagesByCode = {
                                    secret_message_forbidden: "This device is not authenticated for secret messaging.",
                                    secret_key_invalid: "This device could not publish a valid secure messaging key.",
                                    secret_device_conflict: "This secure device identity is already connected.",
                                    secret_message_invalid: "The relay rejected an invalid secret message.",
                                    secret_message_replay: "The relay rejected a replayed secret message.",
                                    secret_recipient_ambiguous: "The recipient device identity is ambiguous.",
                                    secret_recipient_unavailable: "The recipient is no longer available for secret messaging.",
                                };
                                const message = messagesByCode[data.error] || "Secret messaging failed.";
                                setSecretMessageError(message);
                                const pending = pendingSecretSendsRef.current.get(data.id);
                                if (pending) {
                                    clearTimeout(pending.deliveryTimer);
                                    if (pending.retryTimer)
                                        clearTimeout(pending.retryTimer);
                                    pendingSecretSendsRef.current.delete(data.id);
                                    pending.reject(new Error(message));
                                }
                            }
                            break;
                        case 'system_notice': {
                            if (data.notice !== 'scheduled_meeting_imminent' && data.notice !== 'conference_closed_for_meeting')
                                break;
                            const key = `${data.notice}:${data.startsAt || ''}`;
                            if (lastNoticeKeyRef.current === key)
                                break;
                            lastNoticeKeyRef.current = key;
                            setSystemNotice({
                                notice: data.notice,
                                meetingTitle: typeof data.meetingTitle === 'string' ? data.meetingTitle : null,
                                startsAt: typeof data.startsAt === 'string' ? data.startsAt : null,
                                timestamp: data.timestamp || Date.now(),
                            });
                            break;
                        }
                        case 'pong':
                            break;
                        default:
                            break;
                    }
                }
                catch (err) {
                    console.error('[useChatWebSocket] Failed to parse message:', err);
                }
            };
            ws.onclose = () => {
                if (!mountedRef.current ||
                    wsRef.current !== ws ||
                    socketGenerationRef.current !== socketGeneration)
                    return;
                console.log('[useChatWebSocket] Disconnected, will reconnect in 3s');
                commonGenerationRef.current = null;
                rejectCommonRequests('chat_disconnected');
                pendingDecryptRef.current = [];
                if (pendingTimerRef.current)
                    clearInterval(pendingTimerRef.current);
                pendingTimerRef.current = null;
                recordSecretDiagnostic({ kind: 'socket', event: 'disconnected' });
                clearPendingIncomingSecrets();
                setIsConnected(false);
                if (pingTimerRef.current) {
                    clearInterval(pingTimerRef.current);
                    pingTimerRef.current = null;
                }
                if (walletAuthTimerRef.current) {
                    clearInterval(walletAuthTimerRef.current);
                    walletAuthTimerRef.current = null;
                }
                walletAuthAckedRef.current = false;
                adminPresenceAckedRef.current = false;
                if (adminPresenceRetryRef.current) {
                    clearInterval(adminPresenceRetryRef.current);
                    adminPresenceRetryRef.current = null;
                }
                setOrganizerPresent(false);
                secretKeyRegisteredRef.current = false;
                adminPresenceChallengeRef.current = null;
                adminPresenceSigningRef.current = false;
                adminPresenceAttemptedChallengeRef.current = null;
                if (guestAccessClosedRef.current)
                    return;
                recordSecretDiagnostic({ kind: 'socket', event: 'reconnect_scheduled' });
                reconnectTimerRef.current = setTimeout(() => {
                    if (mountedRef.current)
                        connect();
                }, 3000);
            };
            ws.onerror = (err) => {
                if (wsRef.current !== ws || socketGenerationRef.current !== socketGeneration)
                    return;
                console.error('[useChatWebSocket] WebSocket error:', err);
            };
        };
        connect();
        return () => {
            mountedRef.current = false;
            cleanup();
            setIsConnected(false);
        };
    }, [
        normalizedRoom, guestToken, deviceSessionToken, suppliedDeviceId,
        retransmitPendingSecretSends, advertiseSecretKey,
        sendSecretConversationOpen,
        attemptAdminPresenceAuthorization, clearPendingIncomingSecrets,
        drainPendingIncomingSecrets, processIncomingSecret, recordSecretDiagnostic,
        rescopeSecretDiagnostics, clearSecretStateForAuthBoundary,
    ]);
    const sendMessage = useCallback(async (text) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            console.warn('[useChatWebSocket] Cannot send message: not connected');
            throw new Error('chat_message_disconnected');
        }
        try {
            const socket = wsRef.current;
            const room = walletAddressRef.current;
            if (!room)
                throw new Error('chat_message_session_changed');
            const generation = commonGenerationRef.current;
            if (generation === null)
                throw new Error('chat_generation_unavailable');
            if (!commonAckVersionRef.current)
                throw new Error('chat_ack_unsupported');
            // A freshly opened wallet socket is not authenticated yet. Wait for
            // wallet_auth_ok rather than dropping the organizer's first message.
            const deadline = Date.now() + 10_000;
            while (!guestTokenRef.current && !deviceSessionTokenRef.current && !walletAuthAckedRef.current) {
                if (socket !== wsRef.current || socket.readyState !== WebSocket.OPEN ||
                    room !== walletAddressRef.current || Date.now() >= deadline) {
                    throw new Error('chat_message_auth_unavailable');
                }
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            const encryptedPayload = await encryptFnRef.current(text);
            if (socket !== wsRef.current || socket.readyState !== WebSocket.OPEN || room !== walletAddressRef.current ||
                generation !== commonGenerationRef.current) {
                throw new Error('chat_message_session_changed');
            }
            const timestamp = Date.now();
            const id = crypto.randomUUID();
            await new Promise((resolve, reject) => {
                const timer = setTimeout(() => pending.reject(new Error('chat_message_timeout')), 15000);
                const finish = () => { clearTimeout(timer); commonSendRequestsRef.current.delete(id); };
                const pending = {
                    id, room: room.toLowerCase(), socket, generation,
                    resolve: () => { finish(); resolve(); },
                    reject: (error) => { finish(); reject(error); },
                };
                commonSendRequestsRef.current.set(id, pending);
                try {
                    socket.send(JSON.stringify({ type: 'chat_message', generation, id,
                        deviceId: deviceIdRef.current, encryptedPayload, timestamp }));
                }
                catch (error) {
                    pending.reject(error);
                }
            });
            if (socket !== wsRef.current || room !== walletAddressRef.current || generation !== commonGenerationRef.current) {
                throw new Error('chat_message_session_changed');
            }
            const msg = {
                generation,
                id,
                text,
                encryptedPayload,
                deviceId: deviceIdRef.current,
                timestamp,
                isMine: true,
            };
            setMessages((prev) => generation === commonGenerationRef.current && socket === wsRef.current &&
                room === walletAddressRef.current ? [...prev, msg] : prev);
        }
        catch (err) {
            console.error('[useChatWebSocket] Failed to encrypt/send message:', err);
            // Do not resolve as though delivered: callers must retain the draft.
            throw err;
        }
    }, []);
    const sendSecretMessage = useCallback(async (recipientDeviceId, text) => {
        const ws = wsRef.current;
        const room = walletAddressRef.current?.trim().toLowerCase();
        const pair = secretKeyPairRef.current;
        const peer = secretPeerKeysRef.current.get(recipientDeviceId);
        const secretContext = secretAdvertisementContextRef.current;
        const trimmed = text.trim();
        if (!trimmed)
            return;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            const message = "Cannot send a secret message while disconnected.";
            setSecretMessageError(message);
            throw new Error(message);
        }
        if (!room || !pair || !peer) {
            const message = "This participant does not currently support secure direct messages.";
            setSecretMessageError(message);
            throw new Error(message);
        }
        if (!readySecretConversationsRef.current.has(recipientDeviceId)) {
            const message = "Open and approve this secret conversation before sending.";
            setSecretMessageError(message);
            throw new Error(message);
        }
        if (!peer.walletSigned && !peer.safetyConfirmed) {
            const message = "Compare and confirm this participant's safety number before sending.";
            setSecretMessageError(message);
            throw new Error(message);
        }
        const timestamp = Date.now();
        const random = crypto.getRandomValues(new Uint32Array(2));
        const id = `sm-${timestamp}-${random[0].toString(36)}${random[1].toString(36)}`;
        const binding = {
            room,
            senderDeviceId: deviceIdRef.current,
            recipientDeviceId,
            id,
            timestamp,
        };
        try {
            const encryptedPayload = await encryptSecretMessage(trimmed, pair.privateKey, peer.publicKey, binding);
            // Encryption may outlive a scope transition. Never install or send an
            // old-room ciphertext on the replacement room/transport.
            if (secretAdvertisementContextRef.current !== secretContext ||
                walletAddressRef.current?.trim().toLowerCase() !== room ||
                secretKeyPairRef.current !== pair ||
                secretPeerKeysRef.current.get(recipientDeviceId) !== peer ||
                wsRef.current !== ws) {
                throw new Error("Secret message scope changed before encryption completed.");
            }
            const envelope = { type: "secret_message", ...binding, encryptedPayload };
            // Install the waiter before sending.  Browser WebSocket implementations
            // may deliver a local relay response before the next task, so registering
            // afterwards loses a real recipient ACK and incorrectly times out.
            await new Promise((resolve, reject) => {
                const deliveryTimer = setTimeout(() => {
                    pendingSecretSendsRef.current.delete(id);
                    reject(new Error("The relay did not confirm delivery of the secret message."));
                }, SECRET_DELIVERY_TIMEOUT_MS);
                pendingSecretSendsRef.current.set(id, {
                    resolve, reject, envelope, accepted: false, attempts: 1,
                    deliveryTimer, retryTimer: null,
                });
                try {
                    ws.send(JSON.stringify(envelope));
                }
                catch (error) {
                    clearTimeout(deliveryTimer);
                    pendingSecretSendsRef.current.delete(id);
                    reject(error instanceof Error ? error : new Error("Secret message connection closed."));
                }
            });
            const local = {
                ...binding,
                text: trimmed,
                peerDeviceId: recipientDeviceId,
                isMine: true,
            };
            setSecretMessages((current) => ({
                ...current,
                [recipientDeviceId]: [...(current[recipientDeviceId] || []), local].slice(-200),
            }));
            setSecretMessageError(null);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "Secret message encryption failed.";
            setSecretMessageError(message);
            throw err;
        }
    }, []);
    const canSecretMessage = useCallback((recipientDeviceId) => {
        const peer = secretPeerKeysRef.current.get(recipientDeviceId);
        if (peer && peer.expiresAt <= Date.now()) {
            secretPeerKeysRef.current.delete(recipientDeviceId);
            recordSecretDiagnostic({
                kind: 'readiness_invalidated',
                reason: 'peer_advertisement_expiry',
            });
            recordSecretDiagnostic({ kind: 'advertisement', event: 'expired' });
            return false;
        }
        return !!peer && (peer.walletSigned || peer.safetyConfirmed);
    }, [recordSecretDiagnostic, roomDevices, secretPeerVersion]);
    const isSecretConversationReady = useCallback((recipientDeviceId) => (canSecretMessage(recipientDeviceId) &&
        readySecretConversationsRef.current.has(recipientDeviceId)), [canSecretMessage, secretConversationVersion]);
    const secretConversationRequests = [...remoteSecretConversationOpenRef.current]
        .filter((deviceId) => !localSecretConversationOpenRef.current.has(deviceId));
    const confirmSecretSafetyNumber = useCallback((deviceId) => {
        recordSecretDiagnostic({ kind: 'consent', transition: 'approved' });
        localSecretConversationOpenRef.current.add(deviceId);
        setSecretConversationVersion((value) => value + 1);
        sendSecretConversationOpen(deviceId);
        const peer = secretPeerKeysRef.current.get(deviceId);
        if (!peer?.safetyNumber) {
            // Clicking the explicit open action before key catch-up completes is
            // still an explicit approval. Re-advertising our registered key asks the
            // relay to catch this socket up with every current peer advertisement.
            pendingSecretApprovalsRef.current.add(deviceId);
            setSecretMessageError(null);
            advertiseSecretKey();
            return;
        }
        pendingSecretApprovalsRef.current.delete(deviceId);
        peer.safetyConfirmed = true;
        setSecretMessageError(null);
        setSecretPeerVersion((value) => value + 1);
        drainPendingIncomingSecrets(deviceId);
    }, [
        advertiseSecretKey,
        drainPendingIncomingSecrets,
        recordSecretDiagnostic,
        sendSecretConversationOpen,
    ]);
    const secretSafetyNumbers = Object.fromEntries([...secretPeerKeysRef.current.entries()]
        .filter(([, peer]) => !!peer.safetyNumber && !peer.walletSigned)
        .map(([deviceId, peer]) => [deviceId, peer.safetyNumber]));
    const sendCallSignal = useCallback((signal) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            console.warn('[useChatWebSocket] Cannot send call signal: not connected');
            return;
        }
        // Moderation and exclusive screen-share ownership require a
        // wallet-verified socket. Queue them across the short authentication
        // handshake after (re)connect instead of sending an unauthorized request.
        if (AUTHENTICATED_CALL_SUBTYPES.includes(signal.subtype) &&
            !guestTokenRef.current &&
            !walletAuthAckedRef.current) {
            console.warn(`[useChatWebSocket] Socket not wallet-verified yet — queueing protected call signal ${signal.subtype} until wallet_auth_ok`);
            pendingModerationRef.current.push({ signal, queuedAt: Date.now() });
            walletAuthAttemptRef.current?.();
            return;
        }
        try {
            wsRef.current.send(JSON.stringify({
                type: 'call_signal',
                subtype: signal.subtype,
                callId: signal.callId,
                from: deviceIdRef.current,
                to: signal.to,
                payload: signal.payload,
                timestamp: signal.timestamp || Date.now(),
            }));
        }
        catch (err) {
            console.error('[useChatWebSocket] Failed to send call signal:', err);
        }
    }, []);
    sendCallSignalRef.current = sendCallSignal;
    const setArchiveEnabled = useCallback((enabled, auth) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            console.warn('[useChatWebSocket] Cannot set room config: not connected');
            return;
        }
        try {
            wsRef.current.send(JSON.stringify({
                type: 'set_room_config',
                archiveEnabled: enabled,
                auth,
            }));
        }
        catch (err) {
            console.error('[useChatWebSocket] Failed to send room config:', err);
        }
    }, []);
    const clearMessages = useCallback(async () => {
        const room = walletAddressRef.current;
        const socket = wsRef.current;
        const generation = commonGenerationRef.current;
        const signer = clearSignerRef.current;
        if (!room || guestTokenRef.current || !signer || generation === null || !commonAckVersionRef.current ||
            !socket || socket.readyState !== WebSocket.OPEN || clearRequestRef.current) {
            throw new Error('clear_unavailable');
        }
        const timestamp = Date.now();
        const signature = await signer(buildChatClearAuthMessage(room, generation, timestamp));
        if (socket !== wsRef.current || socket.readyState !== WebSocket.OPEN ||
            room !== walletAddressRef.current || generation !== commonGenerationRef.current || clearRequestRef.current) {
            throw new Error('clear_session_changed');
        }
        await new Promise((resolve, reject) => {
            const id = crypto.randomUUID();
            const timer = setTimeout(() => {
                pending.reject(new Error('clear_timeout'));
            }, 15000);
            const finish = () => {
                clearTimeout(timer);
                if (clearRequestRef.current === pending)
                    clearRequestRef.current = null;
            };
            const pending = {
                id, room: room.toLowerCase(), socket, generation,
                resolve: () => { finish(); resolve(); },
                reject: (error) => { finish(); reject(error); },
            };
            clearRequestRef.current = pending;
            try {
                socket.send(JSON.stringify({ type: 'clear_common_chat', requestId: id, generation, auth: { timestamp, signature } }));
            }
            catch (error) {
                clearRequestRef.current.reject(error);
                clearRequestRef.current = null;
            }
        });
    }, []);
    const getMessagesForExport = useCallback(() => {
        return [...messages];
    }, [messages]);
    return {
        messages,
        sendMessage,
        isConnected,
        connectedDevices,
        roomDevices,
        organizerPresent,
        clearMessages,
        getMessagesForExport,
        deviceId: deviceIdRef.current,
        sendCallSignal,
        archiveEnabled,
        archiveStateUnknown,
        setArchiveEnabled,
        systemNotice,
        clearSystemNotice: useCallback(() => setSystemNotice(null), []),
        secretMessages,
        sendSecretMessage,
        canSecretMessage,
        isSecretConversationReady,
        secretConversationRequests,
        secretMessageError,
        secretSafetyNumbers,
        confirmSecretSafetyNumber,
        guestAccessClosed,
        getSecretDiagnostics,
        clearSecretDiagnostics,
        secretDiagnostics: secretDiagnosticsControl,
    };
}
